/**
 * ★★ WO lote 2b — O EXPEDIENTE DA CLÍNICA. Puro: nenhum Firestore, nenhum relógio implícito.
 *
 * Responde três perguntas, e só elas:
 *   1. este dia é dia de trabalho?            (`ehDiaUtil`)
 *   2. qual é a meta DESTE dia?               (`metaDoDia` — sábado vale metade)
 *   3. quanto do expediente de hoje já passou? (`fracaoDoExpediente` — a barra de ritmo)
 *
 * ★★ `agora` SEMPRE entra por parâmetro. A WN travou isto com guarda depois de 7 testes
 * ficarem vermelhos no dia 1º de um mês: função que lê o relógio por dentro não é testável
 * em instante nenhum.
 *
 * ⚠⚠ O DESENHO APROVADO DIZ "expediente 8h–18h" NO RÓTULO DA BARRA — e o dono decidiu, por
 * escrito, **08:00–17:00** (sáb 08:00–12:00). ★ O texto da decisão vence o exemplo do HTML, e
 * por isso o rótulo é **gerado do config**, nunca escrito à mão: assim os dois não podem
 * divergir de novo.
 *
 * ★★★ A-2 (10/out/2026) — DUAS COISAS NOVAS, e as duas são decisão do dono:
 *
 * 1. ★★ **ALMOÇO**, opcional por dia: a faixa passa a ser `{de, ate, almocoDe?, almocoAte?}`.
 *    ⚠⚠ Durante o intervalo **o ritmo PARA** — a barra não anda e não acusa atraso —, e a meta
 *    do dia se distribui **só pelas horas trabalhadas** (`de→almocoDe` + `almocoAte→ate`).
 *    ★ Sábado pode não ter almoço: o campo é por dia, e ausente significa "sem intervalo".
 * 2. ★★★ **EXPEDIENTE POR PESSOA** (`expedienteDaPessoa`): três níveis de reserva —
 *    pessoa → clínica → padrão. ⚠ Nenhuma das funções abaixo mudou de assinatura por isso:
 *    elas já recebiam o `config` por parâmetro, e agora quem chama passa **o da pessoa**.
 *
 * ⚠ `estaNoHorario` é a única função daqui que recebe **fuso**, e tem motivo: ★★★ ela nasceu
 * para o **rodízio** (E7-b, functions), onde o contêiner roda em **UTC** — ver o cabeçalho dela.
 */

import { ehFeriado } from '@/produtividade/feriados';

/** Índice de `Date.getDay()` → chave do config. */
export const DIAS_DA_SEMANA = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sab'];

/**
 * ★ O padrão, decidido pelo dono em 3/out/2026.
 * ⚠ `dom: null` não é "faltou preencher": é "domingo não tem meta". Ver `ehDiaUtil`.
 *
 * ⚠⚠ **SEM ALMOÇO de propósito.** ★★★ Pôr um intervalo no padrão mudaria o ritmo de toda
 * clínica que nunca configurou nada — ★ e o almoço é informação que só quem trabalha ali tem.
 * Ausente = "sem intervalo", que é o comportamento de antes desta peça, idêntico.
 */
export const EXPEDIENTE_PADRAO = Object.freeze({
  seg: { de: '08:00', ate: '17:00' },
  ter: { de: '08:00', ate: '17:00' },
  qua: { de: '08:00', ate: '17:00' },
  qui: { de: '08:00', ate: '17:00' },
  sex: { de: '08:00', ate: '17:00' },
  sab: { de: '08:00', ate: '12:00' },
  dom: null,
});

/**
 * ★★ O FATOR DO SÁBADO, e ele é 0,5 por DECISÃO, não por regra de três.
 *
 * ⚠ A razão de horas daria 4 ÷ 9 = 0,444. O dono disse "metade", e metade é o que a equipe
 * entende ao olhar a linha de meta. ★ Número que a pessoa não consegue reproduzir de cabeça
 * vira desconfiança na tela inteira.
 */
export const FATOR_SABADO = 0.5;

/** 'AAAA-MM-DD' → a chave do dia da semana, sem passar por fuso (a string JÁ é data civil). */
export function diaDaSemanaDe(dia) {
  const [a, m, d] = String(dia ?? '').split('-').map(Number);
  if (!a || !m || !d) return null;
  // ⚠ `Date.UTC` + `getUTCDay`: usar o construtor local faria o dia da semana depender do
  // fuso de quem abre a tela, e um sábado viraria sexta para quem estivesse a leste.
  return DIAS_DA_SEMANA[new Date(Date.UTC(a, m - 1, d)).getUTCDay()];
}

/** A faixa de trabalho do dia, ou `null` quando não se trabalha. */
export function expedienteDoDia(dia, config = EXPEDIENTE_PADRAO) {
  const chave = diaDaSemanaDe(dia);
  if (!chave) return null;
  const faixa = (config ?? EXPEDIENTE_PADRAO)[chave];
  if (!faixa?.de || !faixa?.ate) return null;
  return faixa;
}

export const ehDiaUtil = (dia, config = EXPEDIENTE_PADRAO) => expedienteDoDia(dia, config) !== null;

/** 'HH:MM' → minutos desde a meia-noite. */
const minutos = (hhmm) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm ?? ''));
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

/**
 * ★★★ A-2 — O ALMOÇO DE UMA FAIXA, **ou `null`**.
 *
 * ⚠⚠ **Permitir por enumeração, não proibir:** só conta como intervalo o que é válido por
 * inteiro — ★ os dois horários presentes, `de < ate`, e **dentro** do expediente do dia.
 * ⚠ Qualquer coisa fora disso é **ignorada** (vale como "sem almoço"), e isso é deliberado:
 * ★★ um almoço meio preenchido na tela não pode mudar a meta de ninguém pela metade. ⚠⚠⚠ E
 * **nunca** faz a conta explodir: a alternativa (lançar) derrubaria a tela inteira por um campo.
 *
 * ★ A tela valida ANTES de gravar (`validarFaixa`) e diz o que está errado; aqui é a rede de
 * segurança para o que já estiver no banco.
 */
export function almocoDoDia(dia, config = EXPEDIENTE_PADRAO) {
  const faixa = expedienteDoDia(dia, config);
  if (!faixa) return null;
  return almocoDaFaixa(faixa);
}

/** O mesmo, a partir da faixa já resolvida — é o que as contas usam para não reler o config. */
export function almocoDaFaixa(faixa) {
  const de = minutos(faixa?.almocoDe);
  const ate = minutos(faixa?.almocoAte);
  if (de === null || ate === null || ate <= de) return null;
  const abre = minutos(faixa?.de);
  const fecha = minutos(faixa?.ate);
  if (abre === null || fecha === null || de < abre || ate > fecha) return null;
  return { de: faixa.almocoDe, ate: faixa.almocoAte, minutos: ate - de, deMin: de, ateMin: ate };
}

/**
 * Duração do expediente do dia, em minutos — ⚠⚠ **já descontado o almoço**. Zero quando não
 * se trabalha.
 *
 * ★★★ É aqui que a decisão *"a meta do dia se distribui só pelas horas trabalhadas"* mora: este
 * número é o **denominador do ritmo**. ⚠ Quem muda isto muda a barra de todas as telas.
 */
export function minutosDoExpediente(dia, config = EXPEDIENTE_PADRAO) {
  const faixa = expedienteDoDia(dia, config);
  if (!faixa) return 0;
  const de = minutos(faixa.de);
  const ate = minutos(faixa.ate);
  if (de === null || ate === null || ate <= de) return 0;
  const almoco = almocoDaFaixa(faixa);
  // ⚠ Trava de sanidade: almoço que cobre o expediente inteiro deixaria o denominador em 0 e
  // ★ `fracaoDoExpediente` devolveria `null` ("não há ritmo"), que é o certo — mas nunca negativo.
  return Math.max(0, ate - de - (almoco?.minutos ?? 0));
}

/**
 * ★★ A META DESTE DIA. Sábado vale metade, **arredondando PARA BAIXO** (decisão do dono).
 *
 * ⚠ O arredondamento não é detalhe: com meta 7, para baixo dá sáb **3** e semana **38**; para
 * cima daria 4 e 39. ★ Para baixo, porque sábado é meio expediente — meta arredondada para
 * cima pediria MAIS por hora no sábado do que na terça.
 *
 * ★ Dia sem expediente (domingo) tem meta **zero**, não `null`: ele soma 0 no acumulado e não
 * contamina a conta.
 */
export function metaDoDia(metaDiaria, dia, config = EXPEDIENTE_PADRAO, feriados = []) {
  const base = Number(metaDiaria);
  if (!Number.isFinite(base) || base <= 0) return 0;
  // ★★ Feriado sem meta (02/11/2026): o feriado zera a meta ANTES de qualquer outra regra —
  // inclusive a meia meta do sábado (D4). ⚠ `feriados` é opcional e o padrão é `[]`: quem não
  // passa a lista recebe o número de antes, idêntico (teste de igualdade).
  if (ehFeriado(dia, feriados)) return 0;
  const faixa = expedienteDoDia(dia, config);
  if (!faixa) return 0;
  const ehSabado = diaDaSemanaDe(dia) === 'sab';
  return ehSabado ? Math.floor(base * FATOR_SABADO) : Math.round(base);
}

/** A meta somada de uma lista de dias — é o que colore os totais de semana e de mês. */
export const metaAcumulada = (metaDiaria, dias = [], config = EXPEDIENTE_PADRAO, feriados = []) =>
  (dias ?? []).reduce((soma, d) => soma + metaDoDia(metaDiaria, d, config, feriados), 0);

/**
 * ★★ Quanto do expediente de HOJE já passou — 0 a 1. A marca "agora" da barra de ritmo.
 *
 * @returns `null` quando NÃO HÁ expediente (domingo). ⚠ `null` ≠ 0: zero é "o dia ainda não
 * começou" (e a pessoa está no ritmo); `null` é "não há ritmo a cobrar". Quem consome tem de
 * tratar os dois diferente — é a mesma disciplina do `taxa()` em `acumulado.js`, que devolve
 * `null` em denominador zero em vez de inventar 0%.
 *
 * ⚠ Medido em produção: **8% das interações da equipe acontecem fora de 08:00–17:00** (11 às
 * 6h, 22 às 7h, 29 depois das 18h). ★ Então a saturação nas pontas não é hipótese: antes de
 * abrir devolve 0, e com 0 o esperado é 0 — quem consome **não pode dividir por ele**.
 */
export function fracaoDoExpediente(agora, dia, config = EXPEDIENTE_PADRAO) {
  const total = minutosDoExpediente(dia, config);
  if (!total) return null;
  const faixa = expedienteDoDia(dia, config);
  const inicio = minutos(faixa.de);
  const fim = minutos(faixa.ate);
  // ★ A hora local de quem olha é a hora da clínica: o sistema é de uma clínica só, em SP.
  // ⚠ Quem precisa de fuso explícito é `estaNoHorario` (servidor) — ver o cabeçalho dela.
  const agoraMin = agora.getHours() * 60 + agora.getMinutes();
  if (agoraMin <= inicio) return 0;
  if (agoraMin >= fim) return 1;
  return trabalhadosAte(agoraMin, inicio, almocoDaFaixa(faixa)) / total;
}

/**
 * ★★★ A-2 — MINUTOS TRABALHADOS ATÉ UM INSTANTE. É o coração do "o almoço para o ritmo".
 *
 * ⚠⚠ Três faixas, e a do meio é a que importa:
 *
 * | onde o instante cai | quanto já se trabalhou |
 * |---|---|
 * | antes do almoço | ★ `agora − início` |
 * | ⚠⚠ **DENTRO do almoço** | ★★★ `almoçoDe − início` — **congela**: o relógio anda, o ritmo não |
 * | depois do almoço | ★ a manhã inteira **+** `agora − almoçoAte` — ⚠ a hora do almoço **não entra** |
 *
 * ★★ É o caso (c) do dono por extenso: *"13:30 a cobrança retoma do ponto certo, sem a hora do
 * almoço"*. ⚠ Somar o almoço faria a pessoa voltar do intervalo já devendo uma hora de trabalho.
 */
function trabalhadosAte(agoraMin, inicio, almoco) {
  if (!almoco) return agoraMin - inicio;
  if (agoraMin <= almoco.deMin) return agoraMin - inicio;
  const manha = almoco.deMin - inicio;
  if (agoraMin < almoco.ateMin) return manha;
  return manha + (agoraMin - almoco.ateMin);
}

/**
 * "08:00–17:00", ou "09:00–18:00 · almoço 12:00–13:00".
 *
 * ★ O rótulo SAI DO CONFIG, nunca escrito à mão (ver o cabeçalho). ⚠⚠ E o almoço aparece
 * **porque ele muda a conta**: ★★★ sem dizer o intervalo na tela, a pessoa veria a barra parada
 * ao meio-dia e não teria como saber por quê — ⚠ barra que para sem explicação lê-se como
 * defeito.
 */
export const rotuloDoExpediente = (dia, config = EXPEDIENTE_PADRAO) => {
  const faixa = expedienteDoDia(dia, config);
  if (!faixa) return 'sem expediente';
  const almoco = almocoDaFaixa(faixa);
  return almoco
    ? `${faixa.de}–${faixa.ate} · almoço ${almoco.de}–${almoco.ate}`
    : `${faixa.de}–${faixa.ate}`;
};

/**
 * ★★ Os dias civis entre duas datas, inclusive — 'AAAA-MM-DD'.
 *
 * ⚠ Usa as partes LOCAIS da data, não UTC, de propósito: quem chama é
 * `metas/calculo.js`, cuja janela vem de `limitesDoMes`, que constrói
 * `new Date(ano, m-1, 1)` — meia-noite LOCAL. Ler em UTC devolveria o dia anterior para
 * quem está a oeste de Greenwich, e o 1º do mês sumiria da conta de dias úteis.
 */
export function diasEntre(inicio, fim) {
  if (!(inicio instanceof Date) || !(fim instanceof Date)) return [];
  const dias = [];
  const d = new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate());
  const ultimo = new Date(fim.getFullYear(), fim.getMonth(), fim.getDate());
  // ⚠ Trava de sanidade: janela invertida ou absurda devolve vazio em vez de girar para sempre.
  let voltas = 0;
  while (d <= ultimo && voltas < 400) {
    dias.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    d.setDate(d.getDate() + 1);
    voltas += 1;
  }
  return dias;
}

/**
 * ★★ A META DE UM PERÍODO: meta diária × dias úteis, com o sábado valendo metade.
 *
 * ⚠ NÃO é `metaDiaria × número de dias`. Um mês com 5 sábados tem meta menor que um com 4,
 * e é isso que torna a comparação entre meses honesta.
 */
export const metaDoPeriodo = (metaDiaria, inicio, fim, config = EXPEDIENTE_PADRAO, feriados = []) =>
  metaAcumulada(metaDiaria, diasEntre(inicio, fim), config, feriados);

/**
 * ★ O expediente da clínica, com o padrão como RESERVA.
 *
 * ⚠ Não é `config ?? PADRÃO`: uma config pela metade (só `sab`, por exemplo) deixaria os
 * outros dias sem faixa e a meta inteira iria a zero sem ninguém notar. ★ Cada dia cai no
 * padrão individualmente.
 */
export function expedienteDaClinica(configDaClinica) {
  const salvo = configDaClinica?.expediente;
  if (!salvo || typeof salvo !== 'object') return EXPEDIENTE_PADRAO;
  const saida = {};
  for (const dia of DIAS_DA_SEMANA) {
    // ★ `null` explícito vence o padrão: é como o gestor diz "não se trabalha neste dia".
    saida[dia] = dia in salvo ? salvo[dia] : EXPEDIENTE_PADRAO[dia];
  }
  return saida;
}

/**
 * ★★★ A-2 — O EXPEDIENTE DE UMA PESSOA: três níveis de reserva, dia a dia.
 *
 * `usuarios/{uid}.expediente` → `config.expediente` da clínica → `EXPEDIENTE_PADRAO`.
 *
 * ⚠⚠ Dia a dia, **não** o objeto inteiro: ★ é a mesma razão de `expedienteDaClinica` — uma
 * pessoa que configurou só o sábado deixaria os outros seis dias sem faixa, e a meta da semana
 * dela iria a zero **sem ninguém notar**.
 *
 * ★ `null` explícito vence a reserva: ⚠ é como o gestor diz *"esta pessoa não trabalha neste
 * dia"* — e sem isso ninguém conseguiria ter um dia de folga diferente do da clínica.
 *
 * @param usuario `{ expediente? }` — o documento da pessoa, como a tela já o carrega
 * @param base    o expediente da CLÍNICA já resolvido (`expedienteDaClinica(config)`)
 */
export function expedienteDaPessoa(usuario, base = EXPEDIENTE_PADRAO) {
  const reserva = base && typeof base === 'object' ? base : EXPEDIENTE_PADRAO;
  const salvo = usuario?.expediente;
  if (!salvo || typeof salvo !== 'object') return reserva;
  const saida = {};
  for (const dia of DIAS_DA_SEMANA) {
    saida[dia] = dia in salvo ? salvo[dia] : reserva[dia];
  }
  return saida;
}

/**
 * ★★ A-2 — VALIDAÇÃO DE UMA FAIXA, para a tela dizer o que está errado ANTES de gravar.
 *
 * @returns `null` quando está boa, ou a frase a mostrar.
 *
 * ⚠ Ela existe separada de `almocoDaFaixa` de propósito: ★★★ aquela **ignora** almoço inválido
 * (para o banco antigo não quebrar a tela); ★ esta **recusa**, para o banco novo não receber
 * lixo. ⚠⚠ Uma função que faz as duas coisas erraria uma das duas.
 */
export function validarFaixa(faixa) {
  if (!faixa) return null;                       // ★ dia fechado é válido
  const de = minutos(faixa.de);
  const ate = minutos(faixa.ate);
  if (de === null || ate === null) return 'Preencha o início e o fim do expediente.';
  if (ate <= de) return 'O fim do expediente tem de ser depois do início.';
  const temAlgumAlmoco = Boolean(faixa.almocoDe) || Boolean(faixa.almocoAte);
  if (!temAlgumAlmoco) return null;
  const aDe = minutos(faixa.almocoDe);
  const aAte = minutos(faixa.almocoAte);
  // ⚠ Meio preenchido é erro, não "sem almoço": quem digitou um dos dois quis o intervalo.
  if (aDe === null || aAte === null) return 'Preencha o início e o fim do almoço, ou deixe os dois em branco.';
  if (aAte <= aDe) return 'O fim do almoço tem de ser depois do início.';
  if (aDe < de || aAte > ate) return 'O almoço tem de ficar dentro do expediente.';
  return null;
}

/**
 * ★★★ A-2 — **ESTÁ NO HORÁRIO AGORA?** Pura, e a ÚNICA função daqui que recebe FUSO.
 *
 * ⚠⚠⚠ **POR QUE O FUSO É PARÂMETRO, e por que isto não é exagero.** ★ Ela nasceu para o
 * **rodízio de leads** (E7-b, functions), e **Cloud Functions roda em UTC**. ★★★ A medição de
 * 09/out encontrou o defeito já em produção: `functions/messaging/expediente.js` decide por
 * `agora.getHours()` e **ninguém converte** — ⚠⚠ a decisão "dentro/fora do expediente" está
 * **3 horas deslocada**. ★★ Se o rodízio fosse escrito do mesmo jeito, ele pularia a Talita
 * (09:00–18:00) **das 15h às 18h** — o pior horário possível.
 *
 * ★ Por isso aqui **não existe `getHours()`**: a hora e o dia da semana saem de
 * `Intl.DateTimeFormat` no fuso pedido. ⚠ `America/Sao_Paulo` é o padrão porque é a clínica que
 * existe — ★ mas o parâmetro fica, senão a segunda clínica repete o defeito.
 *
 * ★★ **O almoço conta como FORA**: durante o intervalo a pessoa não está atendendo, e dar-lhe um
 * lead novo é a mesma coisa que dá-lo a quem já foi para casa.
 *
 * @param expediente o expediente **da pessoa** (`expedienteDaPessoa`)
 * @param instante   `Date` — o momento absoluto (UTC por dentro, como todo `Date`)
 * @param fuso       IANA, ex. `'America/Sao_Paulo'`
 */
export function estaNoHorario(expediente, instante, fuso = 'America/Sao_Paulo') {
  if (!(instante instanceof Date) || Number.isNaN(instante.getTime())) return false;
  const local = partesNoFuso(instante, fuso);
  if (!local) return false;
  const faixa = expedienteDoDia(local.dia, expediente);
  if (!faixa) return false;
  const de = minutos(faixa.de);
  const ate = minutos(faixa.ate);
  if (de === null || ate === null || ate <= de) return false;
  if (local.minutosDoDia < de || local.minutosDoDia >= ate) return false;
  const almoco = almocoDaFaixa(faixa);
  // ⚠ `>= deMin` e `< ateMin`: às 13:00 em ponto a pessoa JÁ voltou (é o caso (c) do dono).
  if (almoco && local.minutosDoDia >= almoco.deMin && local.minutosDoDia < almoco.ateMin) return false;
  return true;
}

/**
 * Data civil ('AAAA-MM-DD') e minutos do dia de um instante, **no fuso pedido**.
 *
 * ⚠⚠ `formatToParts` em vez de `format` + regex: ★ o texto formatado muda com a locale e com a
 * versão do ICU; as **partes** são nomeadas e estáveis. ★★ E a data civil volta como string para
 * reaproveitar `diaDaSemanaDe`, que já é testada — ⚠ mapear nome de dia da semana por locale
 * (`'Mon'`, `'seg.'`, `'пн'`) seria uma terceira régua de data na casa.
 */
function partesNoFuso(instante, fuso) {
  let partes;
  try {
    partes = new Intl.DateTimeFormat('en-CA', {
      timeZone: fuso || 'America/Sao_Paulo',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(instante);
  } catch {
    // ⚠ Fuso inválido lança. Devolver `null` faz `estaNoHorario` dizer "fora" — ★ e "fora" é o
    // lado seguro: no rodízio, a regra "se ninguém está no horário, reparte entre todas" cobre.
    return null;
  }
  const p = {};
  for (const parte of partes) p[parte.type] = parte.value;
  if (!p.year || !p.month || !p.day) return null;
  // ★ `hour12: false` pode devolver "24" à meia-noite em alguns motores — vira 0.
  const hora = Number(p.hour) % 24;
  const minuto = Number(p.minute);
  if (!Number.isFinite(hora) || !Number.isFinite(minuto)) return null;
  const dia = `${p.year}-${p.month}-${p.day}`;
  /**
   * ★ `diaDaSemana` NUMÉRICO (0 = domingo) além da chave de texto — ⚠ espelho de
   * `functions/leads/horario.js`, onde o horário do **robô** é indexado por `getDay()`.
   * ★★ A tela não usa este campo hoje; ⚠ ele existe para as duas cópias não divergirem.
   */
  return {
    dia,
    minutosDoDia: hora * 60 + minuto,
    diaDaSemana: DIAS_DA_SEMANA.indexOf(diaDaSemanaDe(dia)),
  };
}
